import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

const Section = ({ number, title, children }) => (
  <motion.div {...fade()} className="mb-12">
    <h2 className="font-display text-xl sm:text-2xl font-bold mb-4" style={{ color: "#1A1A1A" }}>
      {number}. {title}
    </h2>
    <div className="space-y-3 text-base leading-relaxed" style={{ color: "rgba(26,26,26,0.6)" }}>
      {children}
    </div>
  </motion.div>
);

export default function RefundPolicy() {
  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: "#F5F5F0" }} data-testid="refund-page">
      {/* ── HEADER ── */}
      <header
        className="fixed top-0 left-0 right-0 z-50 border-b"
        style={{ backgroundColor: "rgba(245,245,240,0.85)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderColor: "rgba(0,0,0,0.06)" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="refund-logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_2d4aff0d-e5b7-449b-860f-09baa683a8bc/artifacts/b3uq1zlx_Logo.png"
              alt="Prysh AI"
              className="w-9 h-9 object-contain"
            />
            <span className="font-display text-lg font-semibold" style={{ color: "#1A1A1A" }}>Prysh AI</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium transition-colors"
            style={{ color: "rgba(26,26,26,0.5)" }}
            data-testid="refund-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="pt-32 pb-12 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <motion.h1
            {...fade(0.1)}
            className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]"
            style={{ color: "#FF5A36" }}
            data-testid="refund-headline"
          >
            Refund Policy
          </motion.h1>
          <motion.p {...fade(0.2)} className="mt-4 text-sm" style={{ color: "rgba(26,26,26,0.4)" }}>
            Last Updated: 1 April 2026
          </motion.p>
        </div>
      </section>

      {/* ── INTRO ── */}
      <section className="px-6 md:px-12 pb-8">
        <motion.div {...fade()} className="max-w-3xl mx-auto">
          <p className="text-base leading-relaxed" style={{ color: "rgba(26,26,26,0.6)" }}>
            This Refund Policy outlines the terms under which Prysh AI Labs ("Prysh", "we", "our", or "us") provides refunds for purchases made on our platform.
          </p>
          <p className="text-base leading-relaxed mt-3" style={{ color: "rgba(26,26,26,0.6)" }}>
            By purchasing our services, you agree to this Refund Policy.
          </p>
        </motion.div>
      </section>

      {/* ── SECTIONS ── */}
      <section className="px-6 md:px-12 pb-20">
        <div className="max-w-3xl mx-auto">

          <Section number={1} title="General Policy">
            <p>Due to the nature of digital services and AI-generated content, all purchases are generally non-refundable.</p>
            <p>Once credits are used or content is generated, the service is considered delivered.</p>
          </Section>

          <Section number={2} title="Eligibility for Refunds">
            <p>We may provide a refund in the following cases:</p>
            <p className="font-semibold mt-4" style={{ color: "#1A1A1A" }}>a) Technical Issues</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>You were unable to use the platform due to a verified technical issue</li>
              <li>The issue was not resolved within a reasonable time</li>
            </ul>
            <p className="font-semibold mt-4" style={{ color: "#1A1A1A" }}>b) Duplicate or Incorrect Charges</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>You were charged multiple times for the same transaction</li>
              <li>Incorrect billing due to system error</li>
            </ul>
          </Section>

          <Section number={3} title="Non-Refundable Cases">
            <p>Refunds will not be provided for:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Dissatisfaction with AI-generated output quality</li>
              <li>Poor ad performance or business results</li>
              <li>Partial usage of credits</li>
              <li>Change of mind after purchase</li>
              <li>Failure to review platform capabilities before purchase</li>
            </ul>
            <p className="font-medium mt-2" style={{ color: "#1A1A1A" }}>Prysh provides tools, not guaranteed outcomes.</p>
          </Section>

          <Section number={4} title="Subscription Plans (If Applicable)">
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Subscription fees are billed in advance</li>
              <li>No refunds for partial billing periods</li>
              <li>You may cancel anytime to avoid future charges</li>
            </ul>
          </Section>

          <Section number={5} title="Refund Process">
            <p>To request a refund, contact us at:</p>
            <p>
              <a href="mailto:info@pryshai.com" className="font-medium hover:underline" style={{ color: "#FF5A36" }}>info@pryshai.com</a>
            </p>
            <p>Include:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Registered email ID</li>
              <li>Transaction details</li>
              <li>Reason for request</li>
            </ul>
            <p>We aim to review requests within 5–7 business days.</p>
          </Section>

          <Section number={6} title="Mode of Refund">
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Approved refunds will be processed to the original payment method</li>
              <li>Processing timelines depend on your payment provider (typically 5–10 business days)</li>
            </ul>
          </Section>

          <Section number={7} title="Abuse & Fraud Protection">
            <p>We reserve the right to:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Deny refund requests that appear abusive or fraudulent</li>
              <li>Suspend accounts attempting to exploit refund policies</li>
            </ul>
          </Section>

          <Section number={8} title="Changes to Policy">
            <p>We may update this Refund Policy at any time.</p>
            <p>Continued use of the platform constitutes acceptance of the updated policy.</p>
          </Section>

        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="relative z-10 border-t py-12 px-6 md:px-12" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm mb-4" style={{ color: "rgba(26,26,26,0.5)" }}>
            For questions, contact us at:{" "}
            <a href="mailto:info@pryshai.com" className="font-medium hover:underline" style={{ color: "#FF5A36" }} data-testid="refund-contact-email">
              info@pryshai.com
            </a>
          </p>
          <p className="text-xs" style={{ color: "rgba(26,26,26,0.25)" }}>
            &copy; {new Date().getFullYear()} Prysh AI Labs. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
